import {
  defaultMapCenter,
  defaultMapTextSize,
  emptyIcon,
  iconColorActive,
  iconColorBlack,
  iconColorInactive,
  iconColorLeaseComp,
  iconColorShortlist,
  iconColorYellow,
  PDFPaperSize,
  starIcon,
} from "@/constants/mapStyles";
import type {
  LeaseComp,
  LeaseCompMediaFiles,
} from "@/features/survey/types/survey.leaseComp.types";
import type { SurveyPOICategory } from "@/features/survey/types/survey.poi.types";
import type { PortFeature } from "@/services/mapApi";
import { isInvalidCoordinate } from "@/shared/utils/coordinateValidation";
import mediumPlaceholderImg from "@assets/images/image-placeholder-medium.png";
import {
  checkValueIsUnavailable,
  getArrayChunks,
  hasAddressLine2AsCustom,
  isEmptyObjectFromStr,
  surveyShortlistedOverride,
} from "@shared/utils";
import type { DetailedSurvey } from "@survey-types/survey.core.types";
import type {
  BamMapData,
  MapFeature,
  PoiMarkerData,
  radiusObj,
} from "@survey-types/survey.mapInterface.types";
import type { MapKey } from "@survey-types/survey.mapkeys.types";
import type { MediaFile, MediaState } from "@survey-types/survey.media.types";
import type { MapPolygon } from "@survey-types/survey.polygon.types";
import type { Property as OriginalProperty } from "@survey-types/survey.property.types";
import DOMPurify from "dompurify";
import html2canvas from "html2canvas";
import mapboxgl from "mapbox-gl";

type Property = OriginalProperty & {
  imgUrl?: string;
};

// HTMLCanvasElement.prototype.getContext = ((origFn) =>
//   function (this: HTMLCanvasElement, type: string, attribs?: any): any {
//     const temp = attribs || {};
//     temp.preserveDrawingBuffer = true;
//     return origFn.call(this, type, temp);
//   })(HTMLCanvasElement.prototype.getContext);

/**
 * Sanitize text for safe insertion into HTML using DOMPurify.
 * Removes any potentially dangerous HTML/script content while preserving safe text.
 * This is a comprehensive XSS prevention mechanism superior to manual escaping.
 */
const sanitizeText = (text: string): string => {
  if (!text) return "";
  // DOMPurify.sanitize() removes HTML tags but preserves text content
  // Using {ALLOWED_TAGS: []} ensures no HTML tags are allowed
  return DOMPurify.sanitize(text, { ALLOWED_TAGS: [] }).trim();
};

const getPropertyAddress1 = (value: string = "") => {
  const address = value?.toString();
  return address.replace(/<|>/g, "");
};

const getPropertyAddress2 = (
  property: Property,
  isAddressLine2Custom = true,
) => {
  const {
    address2 = "",
    city = "",
    stateProvince = "",
    postalCode = "",
  } = property;
  const normalizedAddress2 =
    address2?.toString().trim() === "—" ? "" : address2?.toString().trim();
  const hasAddress2 = !!normalizedAddress2;
  const cityStateZip = `${city?.toString()}, ${stateProvince?.toString()} ${postalCode?.toString()}`;
  const address =
    !isAddressLine2Custom && hasAddress2
      ? [normalizedAddress2, cityStateZip].filter(Boolean).join(", ")
      : cityStateZip;
  return address.replace(/<|>/g, "");
};

const getIconData = (property: Property) => {
  let bamMapData = {
    iconSize: defaultMapTextSize,
    iconColor: iconColorInactive,
    radius: "",
    radiusLabel: false,
    radiusColor: "",
    radiusLength: "MI",
  };
  if (property && property?.isCurrentLocation) {
    bamMapData.iconColor = iconColorYellow;
  }
  if (
    property?.bamMapData &&
    typeof JSON.parse(property.bamMapData) === "object" &&
    !isEmptyObjectFromStr(property.bamMapData)
  ) {
    bamMapData = JSON.parse(property.bamMapData);
    if (!("iconSize" in JSON.parse(property.bamMapData))) {
      bamMapData.iconSize = defaultMapTextSize;
    }
    if (!("iconColor" in JSON.parse(property.bamMapData))) {
      if (property.isCurrentLocation) {
        bamMapData.iconColor = iconColorYellow;
      } else {
        bamMapData.iconColor = iconColorInactive;
      }
    }
  }
  if (property?.isClientShortlist) {
    bamMapData.iconColor = iconColorShortlist;
  }
  return bamMapData;
};

const getActiveIconColor = (property: Property) => {
  let color = iconColorActive;
  if (property && property.isCurrentLocation && !property.isClientShortlist) {
    color = iconColorYellow;
    if (property.bamMapData) {
      const bamMapData = JSON.parse(property.bamMapData);
      color = bamMapData.iconColor;
    }
  }
  return color;
};

const getCenterPoint = (properties: Property[]) => {
  if (Array.isArray(properties) && properties.length > 0) {
    const propertiesWithCoordinates = properties.filter(
      (p) => p.longitude && p.latitude && !p.isHidden,
    );
    const calDefaultCenter = propertiesWithCoordinates.reduce(
      (acc, p) => [
        acc[0] + parseFloat(p.longitude),
        acc[1] + parseFloat(p.latitude),
      ],
      [0.0, 0.0],
    );
    const defaultCenter = [
      calDefaultCenter[0] / propertiesWithCoordinates.length,
      calDefaultCenter[1] / propertiesWithCoordinates.length,
    ];
    return defaultCenter;
  }
  return defaultMapCenter;
};

const processPDFCoords = (
  property: Property,
  points: { type: string; features: MapFeature[] },
  bamMapData: BamMapData,
  index: number,
  pointIndex: number,
) => {
  const pointData = {
    id: property.propertyId,
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [
        Number(bamMapData.pdfCoordinates.longitude),
        Number(bamMapData.pdfCoordinates.latitude),
      ],
    },
    properties: {
      id: pointIndex,
      title: "Mapbox",
      group: 1,
      order: index,
      description: `${property.city} ${property.stateProvince}`,
      location: {
        address: property.address1,
        city: property.city,
        stateprov: property.stateProvince,
        postalcode: property.postalCode,
        country: "USA",
      },
      markerConfig: {
        popup: "",
        iconSize: getIconData(property).iconSize,
        iconColor: property.isClientShortlist
          ? iconColorShortlist
          : getIconData(property).iconColor,
        icon: property.isCurrentLocation ? "starIcon" : "emptyIcon",
        textContent: pointIndex,
        textX: "27",
        textY: "38",
        textSize: "32",
        closeButton: false,
        radiusColor: getIconData(property).radiusColor,
        radiusLabel: getIconData(property).radiusLabel,
        radius: getIconData(property).radius,
        radiusLength: getIconData(property).radiusLength,
      },
    },
  };
  const updatedFeatures = [
    ...points.features.slice(0, index),
    ...points.features.slice(index + 1),
    pointData,
  ];

  return {
    ...points,
    features: updatedFeatures,
  };
};

const getMapPointsWithImages = (
  survey: DetailedSurvey,
  mediaState?: MediaState,
  leaseComps?: LeaseComp[],
  leaseMediaState?: LeaseCompMediaFiles,
) =>
  new Promise((resolve) => {
    const isAddressLine2Custom = hasAddressLine2AsCustom(survey);
    const points = {
      type: "FeatureCollection",
      features: [] as MapFeature[],
    };
    if (Array.isArray(survey.properties) && survey.properties.length > 0) {
      let propertyList = survey.properties.filter(
        (prop: Property) => !prop.isHidden,
      );
      let pointIndex = 0;
      propertyList = propertyList.map((item: Property) => {
        const propertyId = `${item.propertyId}`;
        const imgObj =
          mediaState && mediaState[propertyId]
            ? mediaState[propertyId].images.find(
                (img: MediaFile) => !img?.isHidden,
              )
            : null;
        const imgUrl = imgObj?.cloudfrontUrl || mediumPlaceholderImg;
        return {
          ...item,
          imgUrl,
        };
      });
      propertyList.forEach((property: Property, index) => {
        const bamMapData = JSON.parse(property.bamMapData || ("{}" as string));
        const pdfCoordinates = bamMapData?.pdfCoordinates || {};
        // Only geocode when a coordinate doesn't already exist
        if (!property.isCurrentLocation) {
          pointIndex += 1;
        }
        if (
          !property.isHidden &&
          ((property.latitude && property.longitude) ||
            (property.address1 &&
              ((property.city && property.stateProvince) ||
                (property.postalCode && property.postalCode.length >= 5))))
        ) {
          const pointData = {
            id: property.propertyId,
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [
                Number(pdfCoordinates.longitude || property.longitude),
                Number(pdfCoordinates.latitude || property.latitude),
              ] as [number, number],
            },
            properties: {
              id: pointIndex,
              title: "Mapbox",
              group: 1,
              order: index,
              description: `${property.city} ${property.stateProvince}`,
              location: {
                address: property.address1,
                city: property.city,
                stateprov: property.stateProvince,
                postalcode: property.postalCode,
                country: "USA",
              },
              markerConfig: {
                popup: `
                  <div class="tooltip-box" style="color: #003f2d">
                    <div class="tooltip-img"  style="background-image: url(${
                      property.imgUrl
                    })" ></div>
                    <div class="tooltip-desc">
                    <div class="tooltip-address1">${getPropertyAddress1(
                      property.address1,
                    )}</div>
                    <div class="tooltip-address2">${getPropertyAddress2(
                      property,
                      isAddressLine2Custom,
                    )}</div>
                    <div class="popover-base-padding action-container">
                    <button id="utility-edit-property" class="tooltip-action transparent-btn" data-id="${
                      property.propertyId
                    }" data-action="edit-property">
                    <i class="emerald-icon material-icons" aria-hidden="false" title="View details for icon Edit">edit</i>
                    </button>
                    <button id="utility-style-property" class="tooltip-black tooltip-action transparent-btn ${
                      property.isClientShortlist ? "disabled-btn" : ""
                    }" data-id="${
                      property.propertyId
                    }" data-action="style-icon">
                    <i class="emerald-icon paintIcon material-icons ${
                      property.isClientShortlist ? "disabled-icon" : ""
                    }" aria-hidden="false" title="Format Paint">format_paint</i>
                    <span class="tooltiptext-black ${
                      !property.isClientShortlist ? "hideElement" : ""
                    }">Point styling is not enabled on shortlisted properties.</span>
                    </button>
                    <button id="utility-radius-property" class="tooltip-action transparent-btn" data-id="${
                      property.propertyId
                    }" data-action="radius-ring">
                    <i class="emerald-icon selectedIconImage material-symbols-outlined" aria-hidden="false" title="Target">target</i>
                    </button>
                    <button id="utility-toggle-property" class="tooltip-action transparent-btn" data-id="${
                      property.propertyId
                    }" data-action="toggle-property">
                    <i class="emerald-icon selectedIconImage material-icons-outlined" aria-hidden="false" title="Remove Red Eye">remove_red_eye</i>
                    </button>
                    </div>
                    </div>
                  </div>
                `,
                iconSize: getIconData(property).iconSize,
                iconColor: property.isClientShortlist
                  ? iconColorShortlist
                  : getIconData(property).iconColor,
                icon: property.isCurrentLocation ? "starIcon" : "emptyIcon",
                textContent: pointIndex,
                textX: "27",
                textY: "38",
                textSize: "32",
                closeButton: false,
                radiusColor: getIconData(property).radiusColor,
                radiusLabel: getIconData(property).radiusLabel,
                radius: getIconData(property).radius,
                radiusLength: getIconData(property).radiusLength,
              },
            },
          };
          if (
            checkValueIsUnavailable(property.latitude) &&
            checkValueIsUnavailable(property.longitude)
          ) {
            delete points.features[0].geometry.coordinates;
          }
          points.features.push(pointData);
        } else {
          // still want to add a point for properties without coordinates so they appear in the legend and PDF, but without geometry
          const pointData: MapFeature = {
            id: property.propertyId,
            type: "Feature",
            geometry: {
              type: "Point",
            },
            properties: {
              id: pointIndex,
              title: "Mapbox",
              group: 1,
              order: index,
              description: "",
              location: {
                address: property.address1 ?? "",
                city: property.city ?? "",
                stateprov: property.stateProvince ?? "",
                postalcode: property.postalCode ?? "",
              },
              markerConfig: {
                textContent: pointIndex,
              },
            },
          };
          points.features.push(pointData);
        }
        // Check for PDF coordinates
        const parsedBamMapData = property.bamMapData
          ? JSON.parse(property.bamMapData)
          : {
              pdfCoordinates: {},
            };

        const hasLatLong =
          "latitude" in (parsedBamMapData.pdfCoordinates || {});
        if (hasLatLong && surveyShortlistedOverride(property)) {
          // this might be the area!!!!
          // if (checkForPdfCoords && !property.isHidden && hasLatLong) {
          processPDFCoords(
            property,
            points,
            parsedBamMapData,
            index,
            pointIndex,
          );
        }
      });
    }
    // Append lease comp features
    if (Array.isArray(leaseComps) && leaseComps.length > 0) {
      const visibleLeaseComps = leaseComps.filter((lc) => !lc.isHidden);
      let lcPointIndex = 0;
      visibleLeaseComps.forEach((lc, lcIndex) => {
        const latitude = lc.mapData?.latitude || lc.leaseCompFields?.latitude;
        const longitude =
          lc.mapData?.longitude || lc.leaseCompFields?.longitude;
        const propertyAddress = lc.leaseCompFields?.propertyAddress;
        const propertyCity = lc.leaseCompFields?.propertyCity;
        const propertyState = lc.leaseCompFields?.propertyState;
        const propertyPostalCode = lc.leaseCompFields?.propertyPostalCode;
        const imgObj =
          leaseMediaState && leaseMediaState[lc.leaseCompId]
            ? leaseMediaState[lc.leaseCompId].images.find(
                (img) => !img?.isHidden,
              )
            : null;
        const imgUrl = imgObj?.cloudfrontUrl || mediumPlaceholderImg;
        lcPointIndex += 1;
        if (
          (latitude && longitude) ||
          (propertyAddress &&
            ((propertyCity && propertyState) ||
              (propertyPostalCode && propertyPostalCode.length >= 5)))
        ) {
          const label = String(lcPointIndex);

          const cityStateZip = [propertyCity, propertyState, propertyPostalCode]
            .filter(Boolean)
            .join(", ");

          const pointData: MapFeature = {
            id: lc.leaseCompId,
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [Number(longitude), Number(latitude)] as [
                number,
                number,
              ],
            },
            properties: {
              id: lcPointIndex,
              title: "Mapbox",
              group: 1,
              order: lcIndex,
              description: `${propertyCity ?? ""} ${propertyState ?? ""}`,
              location: {
                address: propertyAddress ?? "",
                city: propertyCity ?? "",
                stateprov: propertyState ?? "",
                postalcode: propertyPostalCode ?? "",
              },
              propertyType: "leaseComp",
              markerConfig: {
                popup: `
                  <div class="tooltip-box" style="color: #003f2d">
                  <div class="tooltip-img"  style="background-image: url(${
                    imgUrl
                  })" ></div>
                    <div class="tooltip-desc">
                      <div class="tooltip-address1">${lc.name.replace(/<|>/g, "")}</div>
                      <div class="tooltip-address2">${cityStateZip.replace(/<|>/g, "")}</div>
                    <div class="popover-base-padding action-container">
                    <button id="utility-edit-property" class="tooltip-action transparent-btn" data-id="${
                      lc.leaseCompId
                    }" data-action="edit-property">
                    <i class="emerald-icon material-icons" aria-hidden="false" title="View details for icon Edit">edit</i>
                    </button>
                    <button id="utility-style-property" class="tooltip-action transparent-btn" data-id="${
                      lc.leaseCompId
                    }" data-action="style-icon">
                    <i class="emerald-icon paintIcon material-icons" aria-hidden="false" title="Format Paint">format_paint</i>
                    </button>
                    <button id="utility-radius-property" class="tooltip-action transparent-btn" data-id="${
                      lc.leaseCompId
                    }" data-action="radius-ring">
                    <i class="emerald-icon selectedIconImage material-symbols-outlined" aria-hidden="false" title="Target">target</i>
                    </button>
                    <button id="utility-toggle-property" class="tooltip-action transparent-btn" data-id="${
                      lc.leaseCompId
                    }" data-action="toggle-property">
                    <i class="emerald-icon selectedIconImage material-icons-outlined" aria-hidden="false" title="Remove Red Eye">remove_red_eye</i>
                    </button>
                    </div>
                    </div>
                  </div>
                `,
                iconSize: lc.mapData?.iconSize || defaultMapTextSize,
                iconColor: lc.mapData?.iconColor || iconColorLeaseComp,
                icon: "squareIcon",
                textContent: label,
                textX: "27",
                textY: "38",
                textSize: "32",
                closeButton: false,
                radiusColor: lc.mapData?.radiusColor || "",
                radiusLabel: lc.mapData?.radiusLabel || false,
                radius: lc.mapData?.radius || "",
                radiusLength: lc.mapData?.radiusLength || "MI",
              },
            },
          };
          points.features.push(pointData);
        } else {
          // Add lease comp without coordinates so it appears in the legend
          const pointData: MapFeature = {
            id: lc.leaseCompId,
            type: "Feature",
            geometry: {
              type: "Point",
            },
            properties: {
              id: lcPointIndex,
              title: "Mapbox",
              group: 1,
              order: lcIndex,
              description: `${propertyCity ?? ""} ${propertyState ?? ""}`,
              location: {
                address: propertyAddress ?? "",
                city: propertyCity ?? "",
                stateprov: propertyState ?? "",
                postalcode: propertyPostalCode ?? "",
              },
              propertyType: "leaseComp",
              markerConfig: {},
            },
          };
          points.features.push(pointData);
        }
      });
    }
    resolve(points);
  });

const getMapPointsOnly = (survey: DetailedSurvey, forPdf = false) =>
  new Promise((resolve) => {
    const points = {
      type: "FeatureCollection",
      features: [] as MapFeature[],
    };

    if (Array.isArray(survey.properties) && survey.properties.length > 0) {
      const propertyList = survey.properties.filter(
        (prop: Property) =>
          (prop.isClientShortlist && !prop.isHidden) || !prop.isHidden,
      );
      let pointIndex = 0;
      propertyList.forEach((property: Property, index) => {
        const bamMapData = JSON.parse(property.bamMapData || ("{}" as string));
        const pdfCoordinates = bamMapData?.pdfCoordinates || {};
        // Only geocode when a coordinate doesn't already exist
        if (
          (property.latitude && property.longitude) ||
          (property.address1 &&
            ((property.city && property.stateProvince) ||
              (property.postalCode && property.postalCode.length >= 5)))
        ) {
          if (!property.isCurrentLocation) {
            pointIndex += 1;
          }
          let latitude = Number(property.latitude);
          let longitude = Number(property.longitude);
          if (forPdf && pdfCoordinates.latitude) {
            latitude = Number(pdfCoordinates.latitude);
            longitude = Number(pdfCoordinates.longitude);
          }

          const pointData = {
            id: property.propertyId,
            type: "Feature",
            geometry: {
              type: "Point",
              coordinates: [longitude, latitude] as [number, number],
            },
            properties: {
              id: property.isCurrentLocation ? -1 : pointIndex,
              title: "Mapbox",
              group: 1,
              order: index,
              description: `${property.city} ${property.stateProvince}`,
              location: {
                address: property.address1,
                city: property.city,
                stateprov: property.stateProvince,
                postalcode: property.postalCode,
                country: "USA",
              },
              markerConfig: {
                popup: "",
                iconSize: getIconData(property).iconSize,
                iconColor: property.isClientShortlist
                  ? iconColorShortlist
                  : getIconData(property).iconColor,
                icon: property.isCurrentLocation ? "starIcon" : "emptyIcon",
                textContent: pointIndex,
                textX: "27",
                textY: "38",
                textSize: "32",
                closeButton: false,
                radiusColor: getIconData(property).radiusColor,
                radiusLabel: getIconData(property).radiusLabel,
                radius: getIconData(property).radius,
                radiusLength: getIconData(property).radiusLength,
              },
            },
          };
          if (
            checkValueIsUnavailable(property.latitude) &&
            checkValueIsUnavailable(property.longitude)
          ) {
            delete points.features[0].geometry.coordinates;
          }
          points.features.push(pointData);
        }
        // Check for PDF coordinates
        const parsedBamMapData = property.bamMapData
          ? JSON.parse(property.bamMapData)
          : {
              pdfCoordinates: {},
            };
        const hasLatLong =
          "latitude" in (parsedBamMapData.pdfCoordinates || {});
        if (!property.isHidden && hasLatLong) {
          processPDFCoords(
            property,
            points,
            parsedBamMapData,
            index,
            pointIndex,
          );
        }
      });
      resolve(points);
    } else {
      resolve(points);
    }
  });

const getMapSize = (survey: DetailedSurvey): [number, number] => {
  let output: [number, number] = [1056, 816];
  if (survey.pdfPaperSize === PDFPaperSize.Landscape) {
    if (survey.isIncludeHeader && survey.isIncludeLegend) {
      output = [730, 672];
    } else if (!survey.isIncludeHeader && !survey.isIncludeLegend) {
      output = [1056, 766];
    } else if (survey.isIncludeHeader && !survey.isIncludeLegend) {
      output = [1056, 672];
    } else if (!survey.isIncludeHeader && survey.isIncludeLegend) {
      output = [730, 766];
    }
  } else if (survey.pdfPaperSize === PDFPaperSize.Portrait) {
    if (survey.isIncludeHeader && survey.isIncludeLegend) {
      const height = 1056 - 48 - 94;
      output = [814, height];
    } else if (!survey.isIncludeHeader && survey.isIncludeLegend) {
      const height = 1008 - 48;
      output = [814, height];
    } else if (!survey.isIncludeHeader && !survey.isIncludeLegend) {
      output = [814, 1006];
    } else if (survey.isIncludeHeader && !survey.isIncludeLegend) {
      output = [814, 912];
    }
  }
  return output;
};

const getMapEleBoxSize = (prefix: string, index: number): [number, number] => {
  let output: [number, number] = [0, 0];
  const mapEle = document.querySelectorAll(
    `#${prefix}-${index} #map`,
  ) as NodeListOf<HTMLElement>;
  if (mapEle && mapEle[0]) {
    const ele = mapEle[0] as HTMLElement;
    output = [ele.clientWidth, ele.clientHeight];
  }
  return output;
};

const getMapCount = (
  isIncludeLegend: boolean,
  isIncludeHeader: boolean,
  properties: Property[],
) => {
  let propertyChunks = [];
  if (isIncludeHeader) {
    propertyChunks = getArrayChunks(properties, 12);
  } else {
    propertyChunks = getArrayChunks(properties, 14);
  }
  const count = isIncludeLegend ? propertyChunks.length : 1;
  return count;
};

// Helper function to convert Material Icons text to image data
const convertMaterialIconsToImages = async () => {
  // Wait for fonts to load
  await document.fonts.ready;

  // Find all SVG text elements with Material Icons
  const mapContainer = document.getElementById("marketmap-0");
  if (!mapContainer) return;

  // Find all text elements and check if they use Material Icons (via attribute or style)
  const allTexts = mapContainer.querySelectorAll("text");
  const materialIconTexts = Array.from(allTexts).filter((text) => {
    const fontFamily =
      text.getAttribute("font-family") ||
      (text as SVGTextElement).style.fontFamily;
    return fontFamily && fontFamily.includes("Material Icons");
  });

  for (const textEl of materialIconTexts) {
    const svgText = textEl as SVGTextElement;
    const iconText = svgText.textContent || "";
    if (!iconText.trim()) continue;

    const fontSize = parseInt(svgText.getAttribute("font-size") || "13");
    const fill = svgText.getAttribute("fill") || "white";

    // Create a high-resolution temporary canvas
    const tempCanvas = document.createElement("canvas");
    const scale = 4;
    const size = fontSize * scale;
    tempCanvas.width = size;
    tempCanvas.height = size;
    const ctx = tempCanvas.getContext("2d");

    if (ctx) {
      // Enable high quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.font = `${fontSize * scale}px "Material Icons"`;
      ctx.fillStyle = fill;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(iconText, size / 2, size / 2);

      // Convert to data URL
      const dataUrl = tempCanvas.toDataURL("image/png");

      // Replace the text element with an image element
      const svgNS = "http://www.w3.org/2000/svg";
      const image = document.createElementNS(svgNS, "image");

      const textX = parseFloat(svgText.getAttribute("x") || "12");
      const textY = parseFloat(svgText.getAttribute("y") || "13.5");
      const textAnchor = svgText.getAttribute("text-anchor") || "middle";
      const dominantBaseline =
        svgText.getAttribute("dominant-baseline") || "middle";

      // Calculate proper image position based on text anchor and baseline
      let imgX = textX;
      let imgY = textY;

      if (textAnchor === "middle") {
        imgX = textX - fontSize / 2;
      } else if (textAnchor === "end") {
        imgX = textX - fontSize;
      }

      if (dominantBaseline === "middle" || dominantBaseline === "central") {
        imgY = textY - fontSize / 2;
      } else if (dominantBaseline === "hanging") {
        imgY = textY;
      } else if (
        dominantBaseline === "alphabetic" ||
        dominantBaseline === "baseline"
      ) {
        imgY = textY - fontSize * 0.75;
      } else {
        imgY = textY - fontSize / 2;
      }

      image.setAttribute("x", imgX.toString());
      image.setAttribute("y", imgY.toString());
      image.setAttribute("width", fontSize.toString());
      image.setAttribute("height", fontSize.toString());
      image.setAttributeNS("http://www.w3.org/1999/xlink", "href", dataUrl);

      // Replace text with image
      svgText.parentNode?.replaceChild(image, svgText);
    }
  }
};

const marketMapGeneration = async (survey: DetailedSurvey) => {
  const mapEle = document.getElementById("marketmap-0") as HTMLElement;
  const svgLabels = document.getElementsByClassName("svg-labels");
  const radiusLabels = document.getElementsByClassName("radius-label");
  const mapKey = document.getElementById("map-key-section") as HTMLElement;
  const hadRenderedViewOnMapKey =
    mapKey?.classList.contains("rendered-view") ?? false;
  if (mapKey && !hadRenderedViewOnMapKey) mapKey.classList.add("rendered-view");
  const mapKeyTitle = document.getElementsByClassName(
    "map-key-title",
  )[0] as HTMLElement;
  const hadRenderedViewOnMapKeyTitle =
    mapKeyTitle?.classList.contains("rendered-view") ?? false;
  if (mapKeyTitle && !hadRenderedViewOnMapKeyTitle)
    mapKeyTitle.classList.add("rendered-view");
  const mapKeyPois = document.getElementsByClassName("marker-pin-poi");

  // Manipulate img and svg elements for mapKeyPois
  Array.from(mapKeyPois).forEach((element) => {
    const imgElements = element.querySelectorAll("img");
    const svgElements = element.querySelectorAll("svg");

    imgElements.forEach((img) => {
      img.classList.add("!h-[unset]", "mt-3");
    });

    svgElements.forEach((svg) => {
      const currentWidth = svg.getAttribute("width");
      if (currentWidth) {
        const size = parseInt(currentWidth, 10);
        svg.setAttribute("height", `${size * 2}`);
        svg.classList.add("mt-1");
      }
    });
  });

  Array.from(svgLabels).forEach((element) => {
    (element as HTMLElement).style.display = "block";
  });
  Array.from(radiusLabels).forEach((element) => {
    (element as HTMLElement).style.display = "none";
  });

  // Convert Material Icons to images for proper canvas capture
  await convertMaterialIconsToImages();

  // Capture canvas with POI markers included and proper timeout for SVG rendering
  const canvas = await html2canvas(mapEle, {
    ignoreElements: (element) =>
      element.classList.contains("mapboxgl-control-container") ||
      element.classList.contains("mapboxgl-popup"),
    useCORS: true,
    allowTaint: true,
    backgroundColor: null,
    logging: false,
    imageTimeout: 0,
    scale: 2,
  });
  if (mapKey && !hadRenderedViewOnMapKey)
    mapKey.classList.remove("rendered-view");
  if (mapKeyTitle && !hadRenderedViewOnMapKeyTitle)
    mapKeyTitle.classList.remove("rendered-view");

  // Remove manipulations from mapKeyPois img and svg elements
  Array.from(mapKeyPois).forEach((element) => {
    const imgElements = element.querySelectorAll("img");
    const svgElements = element.querySelectorAll("svg");

    imgElements.forEach((img) => {
      img.classList.remove("!h-[unset]", "mt-3");
      img.style.height = "";
    });

    svgElements.forEach((svg) => {
      const currentWidth = svg.getAttribute("width");
      if (currentWidth) {
        svg.setAttribute("height", currentWidth);
        svg.classList.remove("mt-1");
      }
    });
  });

  Array.from(svgLabels).forEach((element) => {
    (element as HTMLElement).style.display = "none";
  });
  Array.from(radiusLabels).forEach((element) => {
    (element as HTMLElement).style.display = "block";
  });

  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, "image/png");
  });

  if (!blob) {
    throw new Error("Failed to generate canvas blob");
  }

  const payloadMedia = {
    surveyId: survey.id,
    mediaType: "MARKETMAP",
  };
  const formData = new FormData();
  formData.append("file", blob, "marketmap.png");
  formData.append("media", JSON.stringify(payloadMedia));

  return formData;
};

const getPropertyAddress = (propertyDetails: Property) => {
  const { city, stateProvince } = propertyDetails;
  const address = `${city?.toString() || ""}, ${stateProvince?.toString() || ""}`;
  return address.replace(/,\s*$/, "");
};

const getFeatureDetails = (
  coordinatesParam: [number, number],
  propertyParam: Property | Record<string, unknown>,
) => {
  const isProperty = (obj: unknown): obj is Property =>
    typeof obj === "object" && obj !== null && "propertyId" in obj;
  return {
    id: isProperty(propertyParam) ? propertyParam.propertyId : "",
    type: "Feature",
    geometry: {
      type: "Point",
      coordinates: [+coordinatesParam[0], +coordinatesParam[1]] as [
        number,
        number,
      ],
    },
    properties: {
      id: 1,
      title: "Mapbox",
      group: 1,
      order: 1,
      description: isProperty(propertyParam)
        ? `${propertyParam.city} ${propertyParam.stateProvince}`
        : "",
      location: {
        address: isProperty(propertyParam) ? propertyParam.address1 : "",
        city: isProperty(propertyParam) ? propertyParam.city : "",
        stateprov: isProperty(propertyParam) ? propertyParam.stateProvince : "",
        postalcode: isProperty(propertyParam) ? propertyParam.postalCode : "",
      },
      markerConfig: {
        popup: `<h3>Mapbox</h3><p>${getPropertyAddress(
          isProperty(propertyParam) ? propertyParam : ({} as Property),
        )}</p>`,
        iconSize: "24",
        iconColor:
          isProperty(propertyParam) && propertyParam.isCurrentLocation
            ? iconColorYellow
            : iconColorBlack,
        icon:
          isProperty(propertyParam) && propertyParam.isCurrentLocation
            ? starIcon
            : emptyIcon,
        textX: "27",
        textY: "38",
        textSize: "0",
      },
    },
  };
};

const getPointsForAddressForm = (
  coordinatesParam: [number, number],
  propertyParam: Property | Record<string, unknown>,
  poiData?: PoiMarkerData,
) => {
  const feature = getFeatureDetails(coordinatesParam, propertyParam);
  return {
    type: "FeatureCollection",
    features: [
      {
        ...feature,
        properties: {
          ...feature.properties,
          ...(poiData && {
            propertyType: "poi",
            markerConfig: {
              ...feature.properties.markerConfig,
              pinType: poiData.pinType,
              pointSize: poiData.pointSize,
              iconColor:
                poiData.iconData?.color ??
                feature.properties.markerConfig.iconColor,
              iconName: poiData.iconData?.iconName,
              logoUrl: poiData.logoUrl,
              logoWhiteBackground: poiData?.logoWhiteBackground,
            },
          }),
        },
      },
    ],
  };
};

const addMarinePortLayer = (
  map: mapboxgl.Map,
  marinePorts: PortFeature[],
  portsVisability: boolean,
) => {
  const sourceId = "marine-port";
  const layerId = "marine-port-layer";
  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }

  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }
  map.addSource(sourceId, {
    type: "geojson",
    data: {
      type: "FeatureCollection",
      features: marinePorts.map((eachPort: PortFeature) => {
        const {
          geometry: { coordinates },
          properties: { PORT_NAME },
        } = eachPort;
        return {
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates,
          },
          properties: {
            portName: PORT_NAME,
          },
        };
      }),
    },
  });

  map.addLayer({
    id: layerId,
    type: "circle",
    source: sourceId,
    paint: {
      "circle-radius": 5,
      "circle-color": "yellow",
      "circle-stroke-width": 1,
      "circle-stroke-color": "#999999",
    },
  });

  if (!portsVisability) {
    map.setLayoutProperty(layerId, "visibility", "none");
  }

  let popup: mapboxgl.Popup | null = null;
  map.on("mouseenter", layerId, (e) => {
    map.getCanvas().style.cursor = "pointer";
    if (e && e.features && e.features.length > 0) {
      const { geometry, properties } = e.features[0];
      let coordinates: [number, number] | undefined;

      if (geometry.type === "Point") {
        if (geometry.coordinates.length === 2) {
          coordinates = geometry.coordinates as [number, number];
        }
      }
      if (coordinates) {
        const { portName } = properties as { portName: string };
        const popupContainer = document.createElement("div");
        popupContainer.style.fontFamily = "Arial, sans-serif";
        popupContainer.style.fontSize = "14px";
        popupContainer.style.color = "#333";
        popupContainer.style.display = "flex";
        popupContainer.style.height = "40px";
        popupContainer.style.justifyContent = "center";
        popupContainer.style.alignItems = "center";
        const portLabel = document.createElement("strong");
        portLabel.style.color = "gray";
        portLabel.textContent = portName;
        popupContainer.appendChild(portLabel);
        popup = new mapboxgl.Popup({ closeButton: false })
          .setLngLat(coordinates)
          .setDOMContent(popupContainer)
          .addTo(map);
      }
    }
  });

  map.on("mouseleave", layerId, () => {
    const canvas = map.getCanvas();
    canvas.style.cursor = "";
    if (popup) {
      popup.remove();
      popup = null;
    }
  });
};

const removeRadiusRings = (
  map: mapboxgl.Map,
  _center: [number, number],
  id: string,
) => {
  const sourceId = `radius-ring-${id}`;
  const layerId = `radius-ring-outline-${id}`;
  const labelId = `radius-label-${id}`;
  const { _markers: markers } = map;
  markers?.forEach((marker: unknown) => {
    const { _element: element } = marker as any;
    const isRadiusLabel = (marker as any).getElement().id.includes(labelId);
    if (isRadiusLabel) {
      element.remove();
    }
  });
  const layers = map
    ?.getStyle()
    ?.layers.filter((eachLayer: { id: string }) =>
      eachLayer.id.includes(layerId),
    );
  layers?.forEach((each: { id: string }) => {
    map.removeLayer(each.id);
  });
  const allSources = map?.getStyle()?.sources;
  if (allSources) {
    const matchingSources = Object.keys(allSources).filter((eachSource) =>
      eachSource.includes(sourceId),
    );
    matchingSources?.forEach((each) => {
      map.removeSource(each);
    });
  }
};

const addRadiusRings = (
  map: mapboxgl.Map,
  center: [number, number],
  radius: number,
  color: string,
  radiusLength: string,
  radiusLabel: boolean,
  id: string,
  index: number,
) => {
  const sourceId = `radius-ring-${id}-${index}`;
  const layerId = `radius-ring-outline-${id}-${index}`;
  const labelId = `radius-label-${id}-${index}`;
  const { _markers: markers } = map as any;
  markers?.forEach((marker: any) => {
    const { _element: element } = marker;
    const isRadiusLabel = marker.getElement().id?.includes(labelId);
    if (isRadiusLabel) {
      element.remove();
    }
  });

  if (map.getLayer(layerId)) {
    map.removeLayer(layerId);
  }

  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }

  const circle: GeoJSON.FeatureCollection<GeoJSON.Geometry> = {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        geometry: {
          type: "Polygon",
          coordinates: [
            Array.from({ length: 40 + 1 }, (_, i) => {
              const angle = (i / 40) * 2 * Math.PI;
              // Approximate radius in degrees (not accurate for large radii or near poles)
              const earthRadius = radiusLength === "MI" ? 3958.8 : 6371; // miles or km
              const d = radius / earthRadius;
              const lat1 = (center[1] * Math.PI) / 180;
              const lon1 = (center[0] * Math.PI) / 180;
              const lat2 = Math.asin(
                Math.sin(lat1) * Math.cos(d) +
                  Math.cos(lat1) * Math.sin(d) * Math.cos(angle),
              );
              const lon2 =
                lon1 +
                Math.atan2(
                  Math.sin(angle) * Math.sin(d) * Math.cos(lat1),
                  Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
                );
              return [(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI];
            }),
          ],
        },
        properties: {},
      },
    ],
  };

  map.addSource(sourceId, {
    type: "geojson",
    data: circle,
  });

  map.addLayer({
    id: layerId,
    type: "line",
    source: sourceId,
    paint: {
      "line-color": color,
      "line-width": 3,
    },
  });

  if (radiusLabel && radius > 0) {
    // Calculate label position using mapbox-gl methods instead of turf
    // We'll offset the label eastwards by the radius distance
    const R = radiusLength === "MI" ? 3958.8 : 6371; // Earth radius in miles or km
    const bearing = 0;
    const lat1 = center[1] * (Math.PI / 180);
    const lon1 = center[0] * (Math.PI / 180);
    const d = radius / R;
    const lat2 = Math.asin(
      Math.sin(lat1) * Math.cos(d) +
        Math.cos(lat1) * Math.sin(d) * Math.cos((bearing * Math.PI) / 180),
    );
    const lon2 =
      lon1 +
      Math.atan2(
        Math.sin((bearing * Math.PI) / 180) * Math.sin(d) * Math.cos(lat1),
        Math.cos(d) - Math.sin(lat1) * Math.sin(lat2),
      );
    const labelPosition = {
      geometry: {
        coordinates: [(lon2 * 180) / Math.PI, (lat2 * 180) / Math.PI],
      },
    };

    const parentDiv = document.createElement("div");
    parentDiv.id = labelId;
    const labelDiv = document.createElement("div");
    labelDiv.style.backgroundColor = color;
    labelDiv.style.color = "#fff";
    labelDiv.style.padding = "0px 5px";
    labelDiv.style.borderRadius = "1px";
    labelDiv.innerText = `${radius} ${radiusLength}`;
    labelDiv.className = "radius-label";
    const labelDivSvg = document.createElement("div");
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");

    // Calculate dynamic width based on text content
    const textContent = `${radius} ${radiusLength}`;
    const fontSize = 10;
    const paddingHorizontal = 8; // 4px padding on each side
    const minWidth = 40;
    // Rough estimate: 0.4 * fontSize per character for most fonts
    const estimatedTextWidth = textContent.length * fontSize * 0.4;
    const dynamicWidth = Math.max(
      minWidth,
      estimatedTextWidth + paddingHorizontal,
    );

    svg.setAttribute("width", Math.ceil(dynamicWidth).toString());
    svg.setAttribute("height", "13");
    svg.setAttribute("viewBox", `0 0 ${Math.ceil(dynamicWidth)} 13`);
    svg.setAttribute("class", "radius-svg");

    // Create rectangular background with dynamic width
    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", "2");
    rect.setAttribute("y", "2");
    rect.setAttribute("width", (dynamicWidth - 4).toString());
    rect.setAttribute("height", "10");
    rect.setAttribute("fill", color);
    rect.setAttribute("rx", "1");
    rect.setAttribute("ry", "1");

    const text = document.createElementNS("http://www.w3.org/2000/svg", "text");
    text.setAttribute("x", "50%");
    text.setAttribute("y", "50%");
    text.setAttribute("text-anchor", "middle");
    text.setAttribute("dy", ".4em");
    text.setAttribute("fill", "#fff");
    text.setAttribute("font-size", "10px");
    text.setAttribute("class", "radius-svg-label");
    text.textContent = textContent;

    svg.appendChild(rect);
    svg.appendChild(text);
    labelDivSvg.className = "svg-labels";
    labelDivSvg.style.display = "none";
    labelDivSvg.appendChild(svg);
    parentDiv.appendChild(labelDivSvg);
    parentDiv.appendChild(labelDiv);
    new mapboxgl.Marker(parentDiv)
      .setLngLat([
        labelPosition.geometry.coordinates[0],
        labelPosition.geometry.coordinates[1],
      ]) // Position the label
      .addTo(map);
  }
};

const isValidCoordinates = (coordinates: number[]): boolean => {
  if (!Array.isArray(coordinates) || coordinates.length !== 2) {
    return false;
  }
  const [lng, lat] = coordinates;
  const isLatitudeUnavailable = checkValueIsUnavailable(lat);
  const isLongitudeUnavailable = checkValueIsUnavailable(lng);
  if (isLatitudeUnavailable || isLongitudeUnavailable) {
    return false;
  }
  const latitude = Number(lat);
  const longitude = Number(lng);
  return (
    latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
  );
};

const hasUsableMapContainer = (mapboxComp: mapboxgl.Map): boolean => {
  try {
    const container = mapboxComp.getContainer();
    return container instanceof HTMLElement && container.isConnected;
  } catch {
    return false;
  }
};

const getMarkerConfig = (
  points: { features: MapFeature[] },
  mapboxComp: mapboxgl.Map,
  mapExists = false,
  fitToMarkers = true,
) => {
  if (mapboxComp && hasUsableMapContainer(mapboxComp)) {
    const bounds = new mapboxgl.LngLatBounds();
    if (points.features.length > 0) {
      // Render lease comp pins first, then POI, then property pins (z-index order)
      const sortedFeatures = [...points.features].sort((a, b) => {
        const getOrder = (type: string | undefined) => {
          if (type === "leaseComp") return 0;
          if (type === "poi") return 1;
          return 2;
        };
        return (
          getOrder(a.properties?.propertyType) -
          getOrder(b.properties?.propertyType)
        );
      });
      sortedFeatures.forEach((point: MapFeature) => {
        const { properties, id, geometry } = point;
        const { coordinates } = geometry;
        if (!geometry?.coordinates) return;
        const { markerConfig, propertyType } = properties;
        const {
          iconColor,
          iconSize,
          icon,
          textContent,
          textSize,
          radiusColor,
          radius,
          radiusLength,
          popup,
          radiusLabel,
          pinType,
          iconName,
          logoUrl,
          logoWhiteBackground,
          pointSize,
        } = markerConfig;
        const el = document.createElement("div");
        el.className = `marker-${id}`;
        el.id = id;
        if (propertyType) {
          el.dataset.propertyType = propertyType;
        }
        if (propertyType === "poi") {
          const poiColor = iconColor || "#a23f97";
          const poiSize = Number(pointSize) || 28;
          if (pinType === "LOGO" && logoUrl) {
            const img = document.createElement("img");
            img.setAttribute("src", logoUrl as string);
            img.style.width = `${poiSize}px`;
            // img.style.borderRadius = "calc(infinity * 1px)";
            img.style.objectFit = "contain";
            img.style.backgroundColor = logoWhiteBackground
              ? "#ffffff"
              : "transparent";
            img.style.padding = logoWhiteBackground ? "2px" : "0px";
            el.appendChild(img);
          } else {
            const poiSvg = document.createElementNS(
              "http://www.w3.org/2000/svg",
              "svg",
            );
            poiSvg.setAttribute("width", String(poiSize));
            poiSvg.setAttribute("height", String(poiSize));
            poiSvg.setAttribute("viewBox", "-1 -1 26 26");
            poiSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

            const poiCircle = document.createElementNS(
              "http://www.w3.org/2000/svg",
              "circle",
            );
            poiCircle.setAttribute("cx", "12");
            poiCircle.setAttribute("cy", "12");
            poiCircle.setAttribute("r", "12");
            poiCircle.setAttribute("stroke", "#fff");
            poiCircle.setAttribute("stroke-width", "2");
            poiCircle.setAttribute("fill", poiColor as string);
            poiSvg.appendChild(poiCircle);

            if (iconName && (iconName as string) !== "circle") {
              const poiText = document.createElementNS(
                "http://www.w3.org/2000/svg",
                "text",
              );
              poiText.setAttribute("x", "12");
              poiText.setAttribute("y", "12");
              poiText.setAttribute("text-anchor", "middle");
              poiText.setAttribute("dominant-baseline", "central");
              poiText.setAttribute("font-family", "Material Icons");
              poiText.setAttribute("font-size", "13");
              poiText.setAttribute("fill", "white");
              poiText.style.fontFamily = "Material Icons";
              poiText.textContent = iconName as string;
              poiSvg.appendChild(poiText);
            }
            el.appendChild(poiSvg);
          }
        } else if (icon === "starIcon") {
          const starSvg = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          starSvg.setAttribute("width", iconSize as string);
          starSvg.setAttribute("height", iconSize as string);
          starSvg.setAttribute("viewBox", "0 0 64 64");
          starSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

          const starCircle = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "circle",
          );
          starCircle.setAttribute("cx", "32");
          starCircle.setAttribute("cy", "32");
          starCircle.setAttribute("r", "28");
          starCircle.setAttribute("fill", iconColor as string);
          starCircle.setAttribute("stroke", "#fff");
          starCircle.setAttribute("stroke-width", "5");

          const starPolygon = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "polygon",
          );
          starPolygon.setAttribute("fill", "#ffffff");
          starPolygon.setAttribute(
            "points",
            "45.57 51.2 32 43.04 18.46 51.2 22.05 35.81 10.11 25.6 25.86 24.26 32 9.6 38.14 24.1 53.89 25.44 41.95 35.78 45.57 51.2",
          );

          starSvg.appendChild(starCircle);
          starSvg.appendChild(starPolygon);
          el.appendChild(starSvg);
        } else if (icon === "squareIcon") {
          const squareSize = Math.round(Number(iconSize) * 0.9).toString();
          const sqSvg = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          sqSvg.setAttribute("width", squareSize);
          sqSvg.setAttribute("height", squareSize);
          sqSvg.setAttribute("viewBox", "0 0 64 64");
          sqSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

          const sqRect = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "rect",
          );
          sqRect.setAttribute("x", "4");
          sqRect.setAttribute("y", "4");
          sqRect.setAttribute("width", "56");
          sqRect.setAttribute("height", "56");
          sqRect.setAttribute("rx", "0");
          sqRect.setAttribute("ry", "0");
          sqRect.setAttribute("fill", iconColor as string);
          sqRect.setAttribute("stroke", "#fff");
          sqRect.setAttribute("stroke-width", "5");

          const sqText = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "text",
          );
          sqText.setAttribute("x", "50%");
          sqText.setAttribute("y", "50%");
          sqText.setAttribute("text-anchor", "middle");
          sqText.setAttribute("dy", ".4em");
          sqText.setAttribute("fill", "#fff");
          sqText.setAttribute("font-size", textSize as string);
          sqText.textContent = (textContent as string) || "";

          sqSvg.appendChild(sqRect);
          sqSvg.appendChild(sqText);
          el.appendChild(sqSvg);
        } else {
          const circleSvg = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "svg",
          );
          circleSvg.setAttribute("width", iconSize as string);
          circleSvg.setAttribute("height", iconSize as string);
          circleSvg.setAttribute("viewBox", "0 0 64 64");
          circleSvg.setAttribute("xmlns", "http://www.w3.org/2000/svg");

          const circleEl = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "circle",
          );
          circleEl.setAttribute("cx", "32");
          circleEl.setAttribute("cy", "32");
          circleEl.setAttribute("r", "29");
          circleEl.setAttribute("fill", iconColor as string);
          circleEl.setAttribute("stroke", "#fff");
          circleEl.setAttribute("stroke-width", "5");

          const circleText = document.createElementNS(
            "http://www.w3.org/2000/svg",
            "text",
          );
          circleText.setAttribute("x", "50%");
          circleText.setAttribute("y", "50%");
          circleText.setAttribute("text-anchor", "middle");
          circleText.setAttribute("dy", ".4em");
          circleText.setAttribute("fill", "#fff");
          circleText.setAttribute("font-size", textSize as string);
          circleText.textContent = (textContent as string) || "";

          circleSvg.appendChild(circleEl);
          circleSvg.appendChild(circleText);
          el.appendChild(circleSvg);
        }
        if (
          coordinates &&
          coordinates.length > 0 &&
          isValidCoordinates(coordinates || [])
        ) {
          // Allow dragging for all marker types (property, lease comp, and POI)
          const isDraggable = true;
          const markerInstance = new mapboxgl.Marker(el, {
            draggable: isDraggable,
          });
          const popupNode = document.createElement("div");
          const appendDefaultPopup = () => {
            const topBar = document.createElement("div");
            topBar.style.borderTop = "2px solid #00a657";
            topBar.style.height = "36px";
            topBar.style.backgroundColor = "#f9f9fc";
            const infoDiv = document.createElement("div");
            infoDiv.style.padding = "0px 15px 10px 17px";
            const latP = document.createElement("p");
            latP.textContent = `Lat: ${coordinates[1] || ""}`;
            const lngP = document.createElement("p");
            lngP.textContent = `Long: ${coordinates[0] || ""}`;
            infoDiv.appendChild(latP);
            infoDiv.appendChild(lngP);
            popupNode.appendChild(topBar);
            popupNode.appendChild(infoDiv);
         };

          if (typeof popup === "string" && popup.trim().length > 0) {
            try {
              // Use DOMParser to avoid innerHTML — DOMParser never executes scripts
              // in the parsed document, eliminating the XSS injection vector.
              const parser = new DOMParser();
              const parsed = parser.parseFromString(popup, "text/html");
              const childNodes = Array.from(parsed?.body?.childNodes ?? []);

              if (childNodes.length > 0) {
                childNodes.forEach((child) => {
                  const importedNode = document.importNode(child, true);
                  if (importedNode) {
                    popupNode.appendChild(importedNode);
                  }
                });
              } else {
                appendDefaultPopup();
              }
            } catch (_error) {
              appendDefaultPopup();
            }
          } else {
            appendDefaultPopup();
          }
          try {
            if (!hasUsableMapContainer(mapboxComp)) {
              return;
            }
          markerInstance
            .setLngLat(coordinates)
            .setPopup(
              new mapboxgl.Popup({ offset: 25 }).setDOMContent(popupNode),
            )
            .addTo(mapboxComp);
          } catch (_error) {
            // Map can be disposed while async marker building is in progress.
            return;
          }

          bounds.extend([coordinates[0], coordinates[1]]);
          if (
            propertyType !== "poi" &&
            mapExists &&
            Array.isArray(radius) &&
            radius?.length
          ) {
            radius.forEach((each: radiusObj, index: number) => {
              if (!hasUsableMapContainer(mapboxComp)) return;
              addRadiusRings(
                mapboxComp,
                coordinates,
                each.value,
                radiusColor as string,
                radiusLength as string,
                radiusLabel as boolean,
                id,
                index,
              );
            });
          } else if (propertyType !== "poi") {
            mapboxComp.once("load", () => {
              if (!hasUsableMapContainer(mapboxComp)) return;
              if (Array.isArray(radius) && radius?.length) {
                radius.forEach((each: radiusObj, index: number) => {
                  addRadiusRings(
                    mapboxComp,
                    coordinates,
                    each.value,
                    radiusColor as string,
                    radiusLength as string,
                    radiusLabel as boolean,
                    id,
                    index,
                  );
                });
              }
            });
          }
        }
      });
      if (fitToMarkers && !bounds.isEmpty()) {
        mapboxComp.fitBounds(bounds, { padding: 50 });
      }
    }
  }
};

const deleteMarkers = (mapboxComp: mapboxgl.Map) => {
  if (!mapboxComp) return;

  // Check if style is loaded before accessing it
  if (!mapboxComp.isStyleLoaded()) {
    console.warn("Map style not loaded yet, skipping layer/source removal");
  } else {
    // Remove radius ring layers
    const style = mapboxComp.getStyle();
    const radiusLayers =
      style?.layers?.filter((layer) =>
        layer.id.includes("radius-ring-outline"),
      ) ?? [];

    for (const layer of radiusLayers) {
      if (mapboxComp.getLayer(layer.id)) {
        mapboxComp.removeLayer(layer.id);
      }
    }

    // Remove radius ring sources
    const allSources = style?.sources;
    if (allSources) {
      const radiusSources = Object.keys(allSources).filter((sourceId) =>
        sourceId.includes("radius-ring"),
      );

      for (const sourceId of radiusSources) {
        if (mapboxComp.getSource(sourceId)) {
          mapboxComp.removeSource(sourceId);
        }
      }
    }
  }

  // Remove markers (always safe to do)
  const markers = (mapboxComp as unknown as { _markers?: mapboxgl.Marker[] })
    ._markers;
  if (markers) {
    for (const marker of markers) {
      marker.getElement()?.remove();
    }
  }
};

const handleMarkerSelection = (marker: mapboxgl.Marker) => {
  const markerElement = marker.getElement();
  if (markerElement) {
    const svgElement = markerElement.querySelector("circle");
    const isPolygonElement = markerElement.querySelector("polygon");
    if (svgElement && !isPolygonElement) {
      const currentFill = svgElement.getAttribute("fill");
      const existingOriginal = svgElement.getAttribute("data-original-fill");

      if (currentFill && !existingOriginal && currentFill !== "#103f60") {
        svgElement.setAttribute("data-original-fill", currentFill);
      }
      svgElement.setAttribute("fill", "#103f60");
    }
  }
};

const createPOIFeatures = (poiPoints: any[], poiCategories: any[]) => {
  if (!poiPoints || !poiCategories) return [];

  const categoryMap = new Map(poiCategories.map((cat) => [cat.extId, cat]));

  const features: any[] = [];

  poiPoints.forEach((poi, index) => {
    // Skip hidden POIs
    if (poi.isHidden) return;

    const category = categoryMap.get(poi.categoryId || "");
    if (!poi.latitude || !poi.longitude) return;

    // Use mapData coordinates if available (PDF override), otherwise use actual coordinates
    const lat = poi.mapData?.latitude
      ? Number(poi.mapData.latitude)
      : Number(poi.latitude);
    const lng = poi.mapData?.longitude
      ? Number(poi.mapData.longitude)
      : Number(poi.longitude);

    if (isNaN(lat) || isNaN(lng)) return;

    // Build full address from individual fields
    const addressParts = [];
    if (poi.addressLine1) addressParts.push(poi.addressLine1);
    if (poi.city) addressParts.push(poi.city);
    if (poi.state) addressParts.push(poi.state);
    if (poi.postalCode) addressParts.push(poi.postalCode);
    const addressLine1 = addressParts.join(", ");
    const locationNotes = (poi.locationNotes || "").trim();
    const sanitizedCategoryName = sanitizeText(category?.categoryName || "POI");
    const sanitizedAddressLine = sanitizeText(addressLine1);
    const sanitizedLocationNotes = sanitizeText(locationNotes);

    // Extract icon data from category
    const pinType = category?.pinType || "ICON";
    const iconColor = category?.iconData?.color || "#a23f97";
    const iconName = category?.iconData?.iconName || "circle";
    const pointSize = category?.pointSize || 28;
    const logoWhiteBackground =
      category?.logoData?.logoWhiteBackground || false;

    // For LOGO type, get the active logo URL
    const logoUrl =
      pinType === "LOGO"
        ? (category?.logoData?.logoLinks?.find((l: any) => l.isActive !== false)
            ?.url ?? category?.logoData?.logoLinks?.[0]?.url)
        : undefined;

    const poiFeature = {
      id: `poi-${poi.extId}`,
      type: "Feature",
      geometry: {
        type: "Point",
        coordinates: [lng, lat] as [number, number],
      },
      properties: {
        id: `poi-${index}`,
        title: sanitizedCategoryName,
        group: 2,
        order: index,
        propertyType: "poi",
        markerConfig: {
          popup: `
              <div class="tooltip-box" style="color: #003f2d">
                <div class="tooltip-desc">
                  <div class="tooltip-address1" style="font-weight: 600;">
                    ${sanitizedCategoryName}
                  </div>
                  ${sanitizedAddressLine ? `<div class="tooltip-address2">${sanitizedAddressLine}</div>` : ""}
                  ${
                    sanitizedLocationNotes
                      ? `
                    <p style="margin: 8px 0 2px; font-weight: 600; font-size: 0.75rem; color: #374151;">Location Notes</p>
                    <p style="margin: 0; white-space: pre-wrap;">${sanitizedLocationNotes}</p>
                  `
                      : ""
                  }
                  <div class="popover-base-padding action-container">
                    <button id="utility-edit-poi" class="tooltip-action transparent-btn" data-id="poi-${poi.extId}" data-action="edit-poi">
                      <i class="emerald-icon material-icons" aria-hidden="false" title="Edit POI">edit</i>
                    </button>
                    <button id="utility-toggle-poi" class="tooltip-action transparent-btn" data-id="poi-${poi.extId}" data-action="toggle-poi">
                      <i class="emerald-icon selectedIconImage material-icons-outlined" aria-hidden="false" title="Remove Red Eye">remove_red_eye</i>
                    </button>
                  </div>
                </div>
              </div>
            `,
          pinType,
          iconColor,
          iconName,
          pointSize,
          logoWhiteBackground,
          logoUrl,
          iconSize: "28",
          icon: "circleIcon",
          textContent: "",
          textX: "32",
          textY: "32",
          textSize: "28",
          closeButton: false,
          radiusColor: "",
          radiusLabel: false,
          radius: [],
          radiusLength: "MI",
        },
      },
    };

    features.push(poiFeature);
  });

  return features;
};

const removeMarkerSelection = (
  marker: mapboxgl.Marker,
  properties: Property[],
) => {
  const markerElement = marker.getElement();
  if (markerElement) {
    const svgElement = markerElement.querySelector("circle");
    if (svgElement) {
      const originalFill = svgElement.getAttribute("data-original-fill");
      if (originalFill) {
        svgElement.setAttribute("fill", originalFill);
        return;
      }

      const property = properties.find(
        (prop) => prop.propertyId === marker.getElement().id,
      );
      let markerColor = "#0976C3";
      const bamMapData = getIconData(property as Property);
      if (bamMapData) {
        markerColor = bamMapData.iconColor || "#0976C3";
      }
      svgElement.setAttribute("fill", markerColor);
    }
  }
};

const getBamMapData = (bamMapData: string) => {
  if (bamMapData && !isEmptyObjectFromStr(bamMapData)) {
    try {
      const parsedData = JSON.parse(bamMapData);
      return parsedData;
    } catch {
      return {};
    }
  } else {
    return {};
  }
};

const getMapKeys = (
  properties: Property[],
  leaseComps?: LeaseComp[],
  poiCategories?: SurveyPOICategory[],
): MapKey[] => {
  const surveyProperties = properties
    ?.filter((each) => !each.isHidden)
    ?.filter((each) => !isInvalidCoordinate(each?.latitude, each?.longitude));
  const propertiesBamData = surveyProperties?.map((eachProperty) => {
    const iconData = getIconData(eachProperty as Property);
    return {
      color: iconData.iconColor.toLowerCase(),
      isCurrentLocation: eachProperty.isCurrentLocation || false,
      category: "Property",
    };
  });

  const visibleLeaseComps = (leaseComps ?? []).filter((c) => !c.isHidden);
  const leaseCompsBamData = visibleLeaseComps
    .map((lc) => ({
      color: (
        lc.mapData?.iconColor ||
        iconColorLeaseComp ||
        "#E47444"
      ).toLowerCase(),
      isCurrentLocation: false,
      category: "LeaseComp",
    }))
    .reduce<any[]>((acc, item) => {
      const exists = acc.some(
        (existing) =>
          existing.color === item.color &&
          existing.category === item.category &&
          existing.isCurrentLocation === item.isCurrentLocation,
      );
      if (!exists) acc.push(item);
      return acc;
    }, []);

  const allBamData = [...(propertiesBamData ?? []), ...leaseCompsBamData];

  const allBamDataObj = new Set(
    allBamData.map((item: any) =>
      JSON.stringify({
        color: item.color?.toLowerCase(),
        isCurrentLocation: item.isCurrentLocation,
        category: item.category,
      }),
    ),
  );
  const uniqueAllBamData = Array.from(allBamDataObj).map((item) =>
    JSON.parse(item),
  );
  uniqueAllBamData.forEach((item, index) => {
    item.order = index;
    item.active = true;
    item.label =
      item.category === "LeaseComp"
        ? "Lease Comp"
        : item.isCurrentLocation
          ? "Current Location"
          : item.color.toLowerCase() === "#003f2d"
            ? "Shortlisted Site"
            : "Potential Site";
  });

  // Add POI categories as map keys
  if (poiCategories && poiCategories.length > 0) {
    poiCategories.forEach((category) => {
      const color = category.iconData?.color || "#a23f97"; // Default POI color

      uniqueAllBamData.push({
        label: category.categoryName,
        color: color.toLowerCase(),
        active: true,
        isCurrentLocation: false,
        category: "POI",
        order: uniqueAllBamData.length,
        referenceId: category.extId,
      });
    });
  }

  return uniqueAllBamData;
};

const getMapKeyRect = (): { width: number; height: number } => {
  const el = document.getElementById("map-key-section");
  if (!el) return { width: 0, height: 0 };
  const { width, height } = el.getBoundingClientRect();
  return { width, height };
};

const getMovedPropertyPinIds = (
  properties: OriginalProperty[] | undefined,
): string[] => {
  const movedIds: string[] = [];
  properties?.forEach((property) => {
    const parsedBamMapData = property.bamMapData
      ? JSON.parse(property.bamMapData)
      : { pdfCoordinates: {} };
    if (
      parsedBamMapData?.pdfCoordinates &&
      Object.keys(parsedBamMapData.pdfCoordinates).length > 0
    ) {
      movedIds.push(property.propertyId);
    }
  });
  return movedIds;
};

const getMovedLeaseCompPinIds = (leaseComps: LeaseComp[]): string[] => {
  const movedIds: string[] = [];
  leaseComps.forEach((comp) => {
    const parsedMapData = comp.mapData;
    if (parsedMapData?.latitude && parsedMapData?.longitude) {
      movedIds.push(comp.leaseCompId);
    }
  });
  return movedIds;
};

const getMovedPOIPinIds = (pois: any[]): string[] => {
  const movedIds: string[] = [];
  pois.forEach((poi) => {
    const parsedMapData = poi.mapData;
    if (parsedMapData?.latitude && parsedMapData?.longitude) {
      movedIds.push(poi.extId);
    }
  });
  return movedIds;
};

const getDuplicateMapKeys = (
  mapKeys: MapKey[],
  categoryNameMap?: Map<string, SurveyPOICategory>,
): MapKey[] => {
  const makeKey = (item: MapKey) => {
    // For POI keys, resolve the category ID (even for legacy keys without explicit IDs)
    let poiCategoryId = item.referenceId;
    if (!poiCategoryId && categoryNameMap && item.category === "POI") {
      const category = categoryNameMap.get(item.label?.trim().toLowerCase());
      poiCategoryId = category?.extId;
    }
    return `${item.color.toLowerCase()}|${item.isCurrentLocation}|${item.category ?? "Property"}|${poiCategoryId ?? ""}`;
  };
  const grouped = mapKeys.reduce<Record<string, MapKey[]>>((acc, key) => {
    const k = makeKey(key);
    acc[k] = acc[k] ? [...acc[k], key] : [key];
    return acc;
  }, {});
  // Keep earliest (lowest order), return the rest as duplicates to remove
  return Object.values(grouped)
    .filter((group) => group.length > 1)
    .flatMap((group) => [...group].sort((a, b) => a.order - b.order).slice(1));
};

/**
 * Resolves whether a map key color should be updated when a single property's
 * icon color changes.
 *
 * Returns the updated MapKey object if an update is needed, or null otherwise.
 *
 * The map key color is updated only when:
 *  - No other visible property of the same location type shares the OLD color
 *    (i.e. this was the sole property using that color).
 *  - No other visible property of the same location type already uses the NEW color
 *    (i.e. there is no existing map key entry the new color should merge into).
 */
const resolveMapKeyColorUpdate = (
  allProperties: Property[],
  changedPropertyId: string,
  isCurrentLocation: boolean,
  previousIconColor: string,
  newIconColor: string,
  mapKeys: MapKey[],
): MapKey | null => {
  const isSameLocationType = (each: Property): boolean =>
    (each.isCurrentLocation ?? false) === (isCurrentLocation ?? false);

  const filterByColor = (targetColor: string): Property[] =>
    allProperties.filter(
      (each) =>
        !each.isHidden &&
        each.propertyId !== changedPropertyId &&
        getIconData(each).iconColor.toLowerCase() ===
          targetColor.toLowerCase() &&
        isSameLocationType(each),
    );

  const propertiesWithSameColor = filterByColor(previousIconColor);
  const propertiesWithNewColor = filterByColor(newIconColor);

  if (
    propertiesWithSameColor.length === 0 &&
    propertiesWithNewColor.length === 0
  ) {
    const matchingMapKey = mapKeys?.find(
      (key) => key.color?.toLowerCase() === previousIconColor.toLowerCase(),
    );
    if (matchingMapKey?.mapKeyId) {
      return { ...matchingMapKey, color: newIconColor };
    }
  }

  return null;
};

/**
 * Resolves whether a map key should be updated when a single property's
 * `isCurrentLocation` flag changes (while its icon color stays the same).
 *
 * Returns the updated MapKey object if an update is needed, or null otherwise.
 *
 * The map key is updated only when:
 *  - No other visible property sharing the SAME color and OLD location type exists
 *    (i.e. this was the sole property of that color + location type combination).
 *  - No other visible property sharing the SAME color and NEW location type exists
 *    (i.e. there is no existing map key entry to merge into).
 */
const resolveMapKeyLocationUpdate = (
  allProperties: Property[],
  changedPropertyId: string,
  iconColor: string,
  previousIsCurrentLocation: boolean,
  newIsCurrentLocation: boolean,
  mapKeys: MapKey[],
): MapKey | null => {
  const filterByLocation = (targetLocation: boolean): Property[] =>
    allProperties.filter(
      (each) =>
        !each.isHidden &&
        each.propertyId !== changedPropertyId &&
        getIconData(each).iconColor.toLowerCase() === iconColor.toLowerCase() &&
        (each.isCurrentLocation ?? false) === targetLocation,
    );

  const propertiesWithSameLocation = filterByLocation(
    previousIsCurrentLocation,
  );
  const propertiesWithNewLocation = filterByLocation(newIsCurrentLocation);

  if (
    propertiesWithSameLocation.length === 0 &&
    propertiesWithNewLocation.length === 0
  ) {
    const matchingMapKey = mapKeys?.find(
      (key) =>
        key.color?.toLowerCase() === iconColor.toLowerCase() &&
        key.isCurrentLocation === previousIsCurrentLocation,
    );
    if (matchingMapKey?.mapKeyId) {
      return { ...matchingMapKey, isCurrentLocation: newIsCurrentLocation };
    }
  }

  return null;
};

// Keeps polygon fill/outline/labels above layers added afterward (e.g. marine ports,
// basemap reloads) so they never get painted over.
const raisePolygonLayers = (map: mapboxgl.Map) => {
  ["polygons-fill", "polygons-outline", "polygons-label"].forEach((layerId) => {
    if (map.getLayer(layerId)) map.moveLayer(layerId);
  });
};

const syncPolygonLayers = ({
  map,
  polygons,
  shouldRenderPolygons,
  currentDrawingPolygon,
  defaultPolygonStyle,
  polygonMouseEnterHandlerRef,
  polygonMouseLeaveHandlerRef,
  polygonMouseLeaveCursorRef,
}: {
  map: mapboxgl.Map;
  polygons: MapPolygon[];
  shouldRenderPolygons: boolean;
  currentDrawingPolygon: number[][];
  defaultPolygonStyle: {
    fillColor: string;
    fillOpacity: number;
    strokeColor: string;
    strokeWidth: number;
  };
  polygonMouseEnterHandlerRef: { current: (() => void) | null };
  polygonMouseLeaveHandlerRef: { current: (() => void) | null };
  polygonMouseLeaveCursorRef: { current: string };
}) => {
  if (!map || !map.isStyleLoaded()) return;

  const getPolygonLabelPoint = (coordinates: number[][][]): number[] => {
    const outerRing = coordinates[0] ?? [];
    const points = outerRing.slice(0, -1);
    if (points.length === 0) return [0, 0];

    const [longitude, latitude] = points.reduce(
      ([longitudeTotal, latitudeTotal], [pointLongitude, pointLatitude]) => [
        longitudeTotal + pointLongitude,
        latitudeTotal + pointLatitude,
      ],
      [0, 0],
    );

    return [longitude / points.length, latitude / points.length];
  };

  const polygonData =
    shouldRenderPolygons && polygons.length > 0
      ? {
          type: "FeatureCollection" as const,
          features: polygons.map((polygon) => ({
            type: "Feature" as const,
            id: polygon.polygonId,
            geometry: {
              type: "Polygon" as const,
              coordinates: polygon.coordinates,
            },
            properties: {
              ...polygon.style,
              name: polygon.name || `Polygon ${polygon.order + 1}`,
              showLabel: polygon.showLabel ?? true,
              showBorder: polygon.showBorder ?? true,
              polygonId: polygon.polygonId,
            },
          })),
        }
      : { type: "FeatureCollection" as const, features: [] as any[] };

  const polygonLabelData =
    shouldRenderPolygons && polygons.length > 0
      ? {
          type: "FeatureCollection" as const,
          features: polygons.map((polygon) => ({
            type: "Feature" as const,
            id: polygon.polygonId,
            geometry: {
              type: "Point" as const,
              coordinates: getPolygonLabelPoint(polygon.coordinates),
            },
            properties: {
              name: polygon.name || `Polygon ${polygon.order + 1}`,
              showLabel: polygon.showLabel ?? true,
              polygonId: polygon.polygonId,
            },
          })),
        }
      : { type: "FeatureCollection" as const, features: [] as any[] };

  const polygonSource = map.getSource("polygons") as
    | mapboxgl.GeoJSONSource
    | undefined;
  if (!polygonSource) {
    map.addSource("polygons", {
      type: "geojson",
      data: polygonData,
    });
  } else {
    polygonSource.setData(polygonData);
  }

  const polygonLabelSource = map.getSource("polygon-labels") as
    | mapboxgl.GeoJSONSource
    | undefined;
  if (!polygonLabelSource) {
    map.addSource("polygon-labels", {
      type: "geojson",
      data: polygonLabelData,
    });
  } else {
    polygonLabelSource.setData(polygonLabelData);
  }

  if (!map.getLayer("polygons-fill")) {
    map.addLayer({
      id: "polygons-fill",
      type: "fill",
      source: "polygons",
      paint: {
        "fill-color": ["get", "fillColor"],
        "fill-opacity": ["get", "fillOpacity"],
      },
    });
  }

  if (!map.getLayer("polygons-outline")) {
    map.addLayer({
      id: "polygons-outline",
      type: "line",
      source: "polygons",
      paint: {
        "line-color": ["get", "strokeColor"],
        "line-width": [
          "case",
          ["==", ["get", "showBorder"], false],
          0,
          ["get", "strokeWidth"],
        ],
        "line-opacity": [
          "case",
          ["==", ["get", "showBorder"], false],
          0,
          ["get", "strokeOpacity"],
        ],
      },
    });
  }

  if (!map.getLayer("polygons-label")) {
    map.addLayer({
      id: "polygons-label",
      type: "symbol",
      source: "polygon-labels",
      layout: {
        "text-field": [
          "case",
          ["==", ["get", "showLabel"], false],
          "",
          ["get", "name"],
        ],
        "text-size": 14,
        "text-font": ["literal", ["DIN Offc Pro Medium", "Arial Unicode MS Regular"]],
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
      },
      paint: {
        "text-color": "#000000",
        "text-halo-color": "#ffffff",
        "text-halo-width": 2,
      },
    });
  }

  raisePolygonLayers(map);

  if (
    !polygonMouseEnterHandlerRef.current ||
    !polygonMouseLeaveHandlerRef.current
  ) {
    const mouseEnterHandler = () => {
      map.getCanvas().style.cursor = "pointer";
    };
    const mouseLeaveHandler = () => {
      map.getCanvas().style.cursor = polygonMouseLeaveCursorRef.current;
    };

    polygonMouseEnterHandlerRef.current = mouseEnterHandler;
    polygonMouseLeaveHandlerRef.current = mouseLeaveHandler;

    map.on("mouseenter", "polygons-fill", mouseEnterHandler);
    map.on("mouseleave", "polygons-fill", mouseLeaveHandler);
    map.on("mouseenter", "polygons-outline", mouseEnterHandler);
    map.on("mouseleave", "polygons-outline", mouseLeaveHandler);
    map.on("mouseenter", "polygons-label", mouseEnterHandler);
    map.on("mouseleave", "polygons-label", mouseLeaveHandler);
  }

  if (map.getLayer("drawing-polygon-fill")) map.removeLayer("drawing-polygon-fill");
  if (map.getLayer("drawing-polygon-outline")) map.removeLayer("drawing-polygon-outline");
  if (map.getLayer("drawing-points")) map.removeLayer("drawing-points");
  if (map.getLayer("drawing-cursor-line")) map.removeLayer("drawing-cursor-line");
  if (map.getSource("drawing-polygon")) map.removeSource("drawing-polygon");
  if (map.getSource("drawing-points")) map.removeSource("drawing-points");
  if (map.getSource("drawing-cursor-line")) map.removeSource("drawing-cursor-line");

  if (currentDrawingPolygon.length > 0) {
    map.addSource("drawing-polygon", {
      type: "geojson",
      data: {
        type: "Feature",
        geometry:
          currentDrawingPolygon.length < 3
            ? {
                type: "LineString",
                coordinates: currentDrawingPolygon,
              }
            : {
                type: "Polygon",
                coordinates: [
                  [...currentDrawingPolygon, currentDrawingPolygon[0]],
                ],
              },
        properties: {},
      },
    });

    map.addLayer({
      id: "drawing-polygon-fill",
      type: "fill",
      source: "drawing-polygon",
      paint: {
        "fill-color": defaultPolygonStyle.fillColor,
        "fill-opacity": defaultPolygonStyle.fillOpacity,
      },
    });

    map.addLayer({
      id: "drawing-polygon-outline",
      type: "line",
      source: "drawing-polygon",
      paint: {
        "line-color": defaultPolygonStyle.strokeColor,
        "line-width": defaultPolygonStyle.strokeWidth,
        "line-dasharray": [2, 2],
      },
    });

    map.addSource("drawing-points", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: currentDrawingPolygon.map((point, i) => ({
          type: "Feature",
          geometry: {
            type: "Point",
            coordinates: point,
          },
          properties: { index: i },
        })),
      },
    });

    map.addLayer({
      id: "drawing-points",
      type: "circle",
      source: "drawing-points",
      paint: {
        "circle-radius": 5,
        "circle-color": "#ffffff",
        "circle-stroke-width": 2,
        "circle-stroke-color": defaultPolygonStyle.strokeColor,
      },
    });

    map.addSource("drawing-cursor-line", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });

    map.addLayer({
      id: "drawing-cursor-line",
      type: "line",
      source: "drawing-cursor-line",
      paint: {
        "line-color": defaultPolygonStyle.strokeColor,
        "line-width": defaultPolygonStyle.strokeWidth,
        "line-dasharray": [2, 2],
      },
    });
  }
};

export {
  addMarinePortLayer,
  addRadiusRings,
  createPOIFeatures,
  deleteMarkers,
  getActiveIconColor,
  getBamMapData,
  getCenterPoint,
  getDuplicateMapKeys,
  getIconData,
  getMapCount,
  getMapEleBoxSize,
  getMapKeyRect,
  getMapKeys,
  getMapPointsOnly,
  getMapPointsWithImages,
  getMapSize,
  getMarkerConfig,
  getMovedLeaseCompPinIds,
  getMovedPOIPinIds,
  getMovedPropertyPinIds,
  getPointsForAddressForm,
  handleMarkerSelection,
  isValidCoordinates,
  marketMapGeneration,
  raisePolygonLayers,
  removeMarkerSelection,
  removeRadiusRings,
  resolveMapKeyColorUpdate,
  resolveMapKeyLocationUpdate,
  syncPolygonLayers,
};
